import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { Loader } from '@/components/Common/Loader';
import { useForter3DSChallenge } from '@/hooks/useForter3DSChallenge';
import { useTranslate } from '@/hooks/useTranslate';
import { cn } from '@/utils/cn';

export const FORTER_3DS_CONTAINER_ID = 'forter-card-3ds-form';

/**
 * Presentational shell for the Forter-orchestrated 3DS challenge. All
 * lifecycle/network logic lives in `useForter3DSChallenge`; this component
 * just attaches the returned ref to the container Forter's SDK injects its
 * iframe into, and exposes the hook's `cancel()` via a close button so the
 * shopper has an escape hatch when the Forter iframe never loads / hangs.
 *
 * Modal chrome is only visible during the interactive window
 * (`hasChallenge && !isCompleting`):
 *   - Pre-decision: SDK is deciding frictionless vs challenge — modal hidden
 *     so the frictionless path never flashes empty chrome.
 *   - Completing:   Forter has removed its iframe and we're awaiting
 *     `/3ds/complete` — modal hidden so the shopper isn't staring at an empty
 *     box; the page's existing ProcessingLoader covers the wait, mirroring
 *     the original "Pay" → response window the shopper already understands.
 *
 * `visibility: hidden` (not `display: none`) is intentional: the container
 * keeps real layout dimensions so the SDK can inject its iframe if/when it
 * decides to.
 */
export const ForterCard3DSForm = () => {
  const { challengeContainerRef, hasChallenge, isCompleting, cancel } = useForter3DSChallenge();
  const { t } = useTranslate();
  const isInteractive = hasChallenge && !isCompleting;

  return (
    <div
      className={cn('fixed inset-0 z-150 flex items-center justify-center bg-black/50', !isInteractive && 'invisible')}
      aria-hidden={!isInteractive}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-0 md:mx-4 pt-2 pb-2 flex flex-col max-h-[50rem]">
        <div className="flex items-center justify-between w-full px-5 mb-4">
          <h2 className="text-xl font-semibold">{t('securePaymentVerification')}</h2>
          <button
            type="button"
            onClick={cancel}
            aria-label={t('cancel')}
            data-testid="forter-3ds-cancel-button"
            className="w-6 h-6 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 cursor-pointer"
          >
            <FontAwesomeIcon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="relative w-full min-h-[25rem]">
          {/* Background spinner shows while Forter loads its iframe. Once the
              iframe is in the DOM it sits above the loader; we force
              `bg-white` on it so its opaque area masks the spinner even if
              the iframe's document body is transparent during load. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Loader />
          </div>
          <div
            ref={challengeContainerRef}
            id={FORTER_3DS_CONTAINER_ID}
            className="relative w-full min-h-[25rem] px-5 overflow-auto [&_iframe]:w-full [&_iframe]:min-h-[25rem] [&_iframe]:border-0 [&_iframe]:bg-white"
          />
        </div>
      </div>
    </div>
  );
};
